import { ApiStatus, LoginExpiredStatusList, UCenterServices } from 'network'
import { isLoopFetchAssets } from 'platform-config'
import { requestIdleCallbackFrame } from 'utils'
import { useChangeCurrency } from '../hooks'
import { useChangeLanguage } from '../hooks/use-change-language'
import { useUserInfoStore } from './use-user-info'

export const useAssetsStore = defineStore('assets', () => {
  const assets = ref<ApiResponse.AssetsData>()
  const timeout = ref<NodeJS.Timeout | null>(null)
  const userInfoStore = useUserInfoStore()
  const assetItemList = ref<ApiResponse.AssetItem[]>([])

  const isShowAssets = ref(true)
  const loading = ref(false)
  const initLoading = ref(true)
  const refreshLoading = ref(false)

  const fetchAssets = async () => {
    stopFetchData()
    if (!userInfoStore.isLogin) return
    if (loading.value) return
    loading.value = true

    const res = await UCenterServices.getAssetsList({
      type: 'all',
      currency: userInfoStore.currentToCurrency,
    })

    refreshLoading.value = false
    loading.value = false
    initLoading.value = false

    if (LoginExpiredStatusList.includes(res.status)) {
      userInfoStore.logout()
      return
    }
    assets.value = res.data
    if (isLoopFetchAssets) {
      requestIdleCallbackFrame(() => {
        timeout.value = setTimeout(() => {
          fetchAssets()
        }, 2000)
      })
    }
  }

  const fetchData = async () => {
    await fetchAssets()
  }

  /** 可用 */
  const availableAssets = computed(() => {
    const data = assets.value?.remain.list.find((item) => item.itemId === 10007)
    return data?.over || '0'
  })
  /** 可用 */
  const getCurrentAvailableAsset = (itemId: ItemId) => {
    const data = assets.value?.remain.list.find((item) => item.itemId === itemId)
    return data?.over || '0'
  }

  const showAssets = () => {
    isShowAssets.value = true
  }

  const hideAssets = () => {
    isShowAssets.value = false
  }

  const toggleAssets = () => {
    isShowAssets.value = !isShowAssets.value
  }

  const loadingItemList = ref(true)

  const fetchAssetItemList = async () => {
    if (!loadingItemList.value) return
    const res = await UCenterServices.getAssetItemList()
    loadingItemList.value = false
    if (res.status === ApiStatus.Success) {
      assetItemList.value = Object.values(res.data)
    }
  }

  const stopFetchData = () => {
    if (timeout.value) {
      clearTimeout(timeout.value)
    }
  }

  /**
   * 刷新资产
   */
  const refresh = async () => {
    if (!timeout.value) return
    refreshLoading.value = true
    stopFetchData()
    fetchAssetItemList()
    await fetchAssets()
  }

  /** 未实现盈亏 */
  const unrealizedProfit = computed(() => {
    return assets.value?.contract.currentProfit
  })

  /** 已实现盈亏 */
  const realizedProfit = computed(() => {
    return assets.value?.contract.lastProfit
  })

  useChangeCurrency(() => {
    if (timeout.value) {
      fetchData()
    }
  })

  useChangeLanguage(
    async () => {
      if (timeout.value) {
        fetchData()
      }
    },
    {
      immediate: false,
    },
  )

  const recommendAssetList = computed(() => {
    return assetItemList.value
      .filter((item) => {
        const cnyItemId = 100001
        // 需要排除人民币和usd
        const usdItemId = 100007
        if ([cnyItemId, usdItemId].includes(item.itemId)) return false
        return true
      })
      .slice(0, 5)
  })

  const getAvailableAssetsByItemId = (itemId: ItemId) => {
    const data = assets.value?.remain.list.find((item) => item.itemId === itemId)
    return data?.over || '0'
  }

  return {
    assets,
    fetchData,
    stopFetchData,
    availableAssets,
    getCurrentAvailableAsset,
    refresh,
    showAssets,
    hideAssets,
    isShowAssets,
    fetchAssetItemList,
    loading,
    /** 未实现盈亏 */
    unrealizedProfit,
    /** 已实现盈亏 */
    realizedProfit,
    assetItemList,
    loadingItemList,
    recommendAssetList,
    toggleAssets,
    refreshLoading,
    fetchAssets,
    initLoading,
    getAvailableAssetsByItemId,
  }
})
